import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "@int-traderjoes/ui-components-library";
import { getActiveAssessment } from "@/utils/productInfoDetailsSelectors";
import VendorAssessmentEditableService from "@/services/vendorAssessmentEditableService";
import type {
  EditableFieldKey,
  UseSectionInfoRequiredToggleArgs,
} from "./useSectionInfoRequiredToggle.types";

function parseEditableFlag(raw: unknown, defaultValue: boolean): boolean {
  if (raw === true) return true;
  if (raw === false) return false;
  if (typeof raw === "number") return raw !== 0;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "true" || s === "yes" || s === "y" || s === "1") return true;
    if (s === "false" || s === "no" || s === "n" || s === "0") return false;
  }
  return defaultValue;
}

function patchVendorEditableFlag(args: {
  record: any;
  vendorGroupIndex: number;
  vendorVersionIndex: number;
  editableFieldKey: EditableFieldKey;
  editable: boolean;
}): any {
  const { record, vendorGroupIndex, vendorVersionIndex, editableFieldKey, editable } = args;
  if (!record || typeof record !== "object") return record;
  const vds = record.vendorDetails;
  if (!Array.isArray(vds) || vds.length === 0) return record;

  const gi = Math.min(Math.max(vendorGroupIndex, 0), vds.length - 1);
  const nextRecord = { ...(record as any) };
  const nextVds = [...vds];
  nextRecord.vendorDetails = nextVds;

  const group = nextVds[gi];
  if (!group || typeof group !== "object") return record;
  const nextGroup: any = { ...(group as any) };
  nextVds[gi] = nextGroup;

  const list = nextGroup.vendorList;
  if (Array.isArray(list) && list.length > 0) {
    const vi = Math.min(Math.max(vendorVersionIndex, 0), list.length - 1);
    const nextList = [...list];
    nextGroup.vendorList = nextList;
    const row = nextList[vi];
    nextList[vi] = { ...(row as any), [editableFieldKey]: editable };
  } else {
    // Fallback: if vendorList not present, store at group level.
    nextGroup[editableFieldKey] = editable;
  }

  return nextRecord;
}

export function useSectionInfoRequiredToggle(args: UseSectionInfoRequiredToggleArgs) {
  const {
    enabled,
    signatureKey,
    vendorGroupIndex,
    vendorVersionIndex,
    sources,
    setSources,
    vendorAssessmentId,
    tabName,
    editableFieldKey,
    defaultEditableWhenMissing,
    setValue,
    setGlobalLoading,
    onDisabled,
    clearTabValidation,
    clearTabValidationIds,
  } = args;

  const [isSaving, setIsSaving] = useState(false);
  const syncSigRef = useRef<string>("");

  const editableFromApi = useMemo((): boolean => {
    if (!enabled) return true;
    for (const src of sources) {
      if (!src) continue;
      const merged = getActiveAssessment(src, vendorGroupIndex, vendorVersionIndex) as
        | Record<string, unknown>
        | null
        | undefined;
      const raw = merged?.[editableFieldKey];
      if (raw == null) continue;
      return parseEditableFlag(raw, defaultEditableWhenMissing);
    }
    return defaultEditableWhenMissing;
  }, [
    enabled,
    sources,
    vendorGroupIndex,
    vendorVersionIndex,
    editableFieldKey,
    defaultEditableWhenMissing,
  ]);

  // Sync the UI value from `/productInfo/details` vendorList[*].{editableFieldKey}.
  useEffect(() => {
    if (!enabled) return;
    const sig = `${signatureKey}|${String(vendorGroupIndex)}|${String(vendorVersionIndex)}|${String(
      editableFromApi
    )}`;
    if (syncSigRef.current === sig) return;
    syncSigRef.current = sig;
    setValue(editableFromApi === false ? "No" : "Yes");
    // If the API indicates the section is not editable, run the disabled handler so callers can clear UI state.
    if (editableFromApi === false) {
      onDisabled?.();
      if (clearTabValidation && Array.isArray(clearTabValidationIds)) {
        clearTabValidationIds.forEach((id) => clearTabValidation(id));
      }
    }
  }, [enabled, signatureKey, vendorGroupIndex, vendorVersionIndex, editableFromApi, setValue]);

  const onChange = useCallback(
    (value: unknown) => {
      const nextValue = String(value ?? "");
      const editable = nextValue.trim().toLowerCase() !== "no";

      const vaId = vendorAssessmentId != null ? String(vendorAssessmentId).trim() : "";
      if (!vaId) {
        showToast({ type: "error", message: "Vendor Assessment ID not available" });
        return;
      }

      setIsSaving(true);
      setGlobalLoading?.(true);

      void (async () => {
        try {
          await VendorAssessmentEditableService.setTabEditable({
            vendorAssessmentId: vaId,
            tabName,
            editable,
          });

          if (Array.isArray(setSources) && setSources.length > 0) {
            setSources.forEach((setter, idx) => {
              if (!setter) return;
              const src = sources[idx] as any;
              setter(
                patchVendorEditableFlag({
                  record: src,
                  vendorGroupIndex,
                  vendorVersionIndex,
                  editableFieldKey,
                  editable,
                })
              );
            });
          }

          setValue(nextValue);
          if (!editable) {
            onDisabled?.();
            if (clearTabValidation && Array.isArray(clearTabValidationIds)) {
              clearTabValidationIds.forEach((id) => clearTabValidation(id));
            }
          }
        } catch {
          showToast({
            type: "error",
            message: "Failed to update section setting. Please try again.",
          });
        } finally {
          setIsSaving(false);
          setGlobalLoading?.(false);
        }
      })();
    },
    [
      vendorAssessmentId,
      tabName,
      setGlobalLoading,
      setValue,
      onDisabled,
      clearTabValidation,
      clearTabValidationIds,
      setSources,
      sources,
      vendorGroupIndex,
      vendorVersionIndex,
      editableFieldKey,
    ]
  );

  return {
    editableFromApi,
    isSaving,
    onChange,
  };
}

